import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { loadGuestAttempts, clearGuestAttempts } from "@/lib/localProgress";

const AuthContext = createContext(null);

const TOKEN_KEY = "aeis_token";
const USER_KEY = "aeis_user";
const GUEST_KEY = "aeis_guest";

function persistSession(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

async function migrateGuestAttempts() {
  const attempts = loadGuestAttempts();
  if (!attempts.length) return 0;
  try {
    const { data } = await api.post("/attempts/bulk", { attempts });
    clearGuestAttempts();
    return data?.inserted || 0;
  } catch {
    return 0;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [guest, setGuest] = useState(() => {
    const raw = localStorage.getItem(GUEST_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Rehydrate current user server-side
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    api.get("/auth/me").then(({ data }) => {
      setUser(data);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
    }).catch(() => {
      clearSession();
      setUser(null);
    });
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      persistSession(data.access_token, data.user);
      const migrated = await migrateGuestAttempts();
      setUser(data.user);
      setGuest(null);
      localStorage.removeItem(GUEST_KEY);
      return { ...data.user, _migrated: migrated };
    } finally { setLoading(false); }
  }, []);

  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persistSession(data.access_token, data.user);
      const migrated = await migrateGuestAttempts();
      setUser(data.user);
      setGuest(null);
      localStorage.removeItem(GUEST_KEY);
      return { ...data.user, _migrated: migrated };
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const startGuest = useCallback((ageCategory) => {
    const level = ageCategory === "7-9" ? "A2" : "B1";
    const g = { name: "Guest", age_category: ageCategory, level, role: "guest" };
    localStorage.setItem(GUEST_KEY, JSON.stringify(g));
    setGuest(g);
    return g;
  }, []);

  const updateProfile = useCallback(async (patch) => {
    if (user) {
      const { data } = await api.patch("/auth/profile", patch);
      setUser(data);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      return data;
    }
    if (guest) {
      const merged = { ...guest, ...patch };
      if (patch.age_category) {
        merged.level = patch.age_category === "7-9" ? "A2" : "B1";
      }
      localStorage.setItem(GUEST_KEY, JSON.stringify(merged));
      setGuest(merged);
      return merged;
    }
  }, [user, guest]);

  const value = useMemo(() => ({
    user, guest, loading,
    currentProfile: user || guest,
    isAuthed: !!user,
    isGuest: !!guest && !user,
    register, login, logout, startGuest, updateProfile,
  }), [user, guest, loading, register, login, logout, startGuest, updateProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
